import { useState, useRef, useEffect, useCallback, useId } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';

interface SelectOption {
  value: string | number;
  label: string;
}

interface PineSelectProps {
  options: SelectOption[];
  value: string | number | null | undefined;
  onChange: (value: string | number) => void;
  placeholder?: string;
  error?: boolean;
}

export default function PineSelect({
  options,
  value,
  onChange,
  placeholder = 'Select...',
  error = false,
}: PineSelectProps) {
  const uid = useId();
  const menuId = `pine-select-${uid}`;

  const [open, setOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});

  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find((o) => o.value === value) ?? null;

  const openMenu = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setMenuStyle({
      position: 'fixed',
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      zIndex: 9999,
    });
    const idx = selectedOption ? options.indexOf(selectedOption) : -1;
    setFocusedIndex(idx);
    setOpen(true);
  }, [selectedOption, options]);

  const closeMenu = useCallback(() => {
    setOpen(false);
    setFocusedIndex(-1);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handleMouseDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        (triggerRef.current && triggerRef.current.contains(target)) ||
        (menuRef.current && menuRef.current.contains(target))
      ) {
        return;
      }
      closeMenu();
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [open, closeMenu]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        openMenu();
      }
      return;
    }
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        closeMenu();
        break;
      case 'ArrowDown':
        e.preventDefault();
        setFocusedIndex((i) => Math.min(i + 1, options.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setFocusedIndex((i) => Math.max(i - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (focusedIndex >= 0) {
          onChange(options[focusedIndex].value);
          closeMenu();
        }
        break;
    }
  };

  const handleSelect = (optValue: string | number) => {
    onChange(optValue);
    closeMenu();
  };

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        type="button"
        role="combobox"
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-controls={open ? menuId : undefined}
        aria-activedescendant={
          open && focusedIndex >= 0 ? `${menuId}-opt-${focusedIndex}` : undefined
        }
        onClick={() => (open ? closeMenu() : openMenu())}
        onKeyDown={handleKeyDown}
        className={`w-full flex items-center justify-between border-[1.5px] rounded-[9px] px-2 py-1.5 text-[12px] bg-white transition-colors focus:outline-none ${
          error
            ? 'border-pine-danger'
            : open
            ? 'border-pine-pine'
            : 'border-pine-line hover:border-pine-pine'
        }`}
      >
        <span className={`truncate ${selectedOption ? 'text-pine-ink' : 'text-pine-ink-soft'}`}>
          {selectedOption?.label ?? placeholder}
        </span>
        <ChevronDown
          className={`w-3 h-3 text-pine-ink-soft flex-shrink-0 transition-transform ${
            open ? 'rotate-180' : ''
          }`}
        />
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            id={menuId}
            role="listbox"
            style={menuStyle}
            className="border-[1.5px] border-pine-line rounded-[9px] bg-white shadow-[0_4px_12px_rgba(0,0,0,0.08)] max-h-[200px] overflow-y-auto"
          >
            {options.map((opt, i) => (
              <button
                key={opt.value}
                id={`${menuId}-opt-${i}`}
                role="option"
                aria-selected={opt.value === value}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); }}
                onClick={() => handleSelect(opt.value)}
                className={`w-full text-left px-3 py-2 text-[12px] transition-colors ${
                  opt.value === value
                    ? 'bg-pine-hover-item text-pine-pine font-medium'
                    : i === focusedIndex
                    ? 'bg-[#F4F9F6] text-pine-ink'
                    : 'hover:bg-[#F4F9F6] text-pine-ink'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}
